import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { notificationService } from './notificationService';

export interface Message {
  id?: string;
  senderId: string;
  text: string;
  imageUrl?: string;
  replyTo?: {
    text: string;
    senderId: string;
  };
  createdAt: Timestamp;
}

export interface Chat {
  id: string;
  participants: string[];
  lastMessage: string;
  lastMessageAt: Timestamp;
  listingId: string;
  listingTitle?: string;
  listingImage?: string;
  otherParticipantName?: string;
  otherParticipantPhoto?: string;
  isOnline?: boolean;
}

export const chatService = {
  async createChat(listingId: string, buyerId: string, sellerId: string): Promise<string> {
    const path = 'chats';
    try {
      // Check if chat already exists for this listing and these participants
      const q = query(collection(db, 'chats'), where('participants', 'array-contains', buyerId));

      const querySnapshot = await getDocs(q);
      const existingChat = querySnapshot.docs.find((doc) => {
        const data = doc.data();
        return data.listingId === listingId && data.participants.includes(sellerId);
      });

      if (existingChat) {
        return existingChat.id;
      }

      // Create new chat
      const docRef = await addDoc(collection(db, 'chats'), {
        participants: [buyerId, sellerId],
        listingId,
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
      });

      return docRef.id;
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, path);
      return '';
    }
  },

  subscribeToUnreadCount(userId: string, callback: (count: number) => void) {
    const q = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', userId),
      orderBy('lastMessageAt', 'desc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        // Count chats that have a lastMessage and lastSenderId is NOT the current user
        const unread = snapshot.docs.filter((doc) => {
          const data = doc.data();
          return (
            data.lastMessage &&
            data.lastSenderId &&
            data.lastSenderId !== userId &&
            !data[`readBy_${userId}`]
          );
        }).length;
        callback(unread);
      },
      () => callback(0)
    );
  },

  subscribeToUserChats(userId: string, callback: (chats: Chat[]) => void) {
    const path = 'chats';
    const q = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', userId),
      orderBy('lastMessageAt', 'desc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const chats = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Chat);
        callback(chats);
      },
      (error) => {
        handleFirestoreError(error, OperationType.LIST, path);
      }
    );
  },

  subscribeToMessages(chatId: string, callback: (messages: Message[]) => void) {
    const path = `chats/${chatId}/messages`;
    const q = query(collection(db, 'chats', chatId, 'messages'), orderBy('createdAt', 'asc'));

    return onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Message);
        callback(messages);
      },
      (error) => {
        handleFirestoreError(error, OperationType.LIST, path);
      }
    );
  },

  async sendMessage(
    chatId: string,
    senderId: string,
    text: string,
    imageUrl?: string,
    replyTo?: { text: string; senderId: string }
  ): Promise<void> {
    const messagesPath = `chats/${chatId}/messages`;
    try {
      // Add message
      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        senderId,
        text,
        imageUrl: imageUrl || null,
        replyTo: replyTo || null,
        createdAt: serverTimestamp(),
      });

      // Fetch chat data to find receiver
      const chatRef = doc(db, 'chats', chatId);
      const chatSnap = await getDoc(chatRef);
      let receiverId = '';

      if (chatSnap.exists()) {
        const chatData = chatSnap.data();
        receiverId = chatData.participants.find((id: string) => id !== senderId) || '';
      }

      // Update chat last message, sender details and read status
      await updateDoc(chatRef, {
        lastMessage: text,
        lastMessageAt: serverTimestamp(),
        lastSenderId: senderId,
        [`readBy_${senderId}`]: true,
        ...(receiverId ? { [`readBy_${receiverId}`]: false } : {}),
      });

      if (receiverId) {
        // Import here to avoid circular dependency if any, but better to import at top.
        // Assuming userService is available via import (will add import at top)
        const { userService } = await import('./userService');
        const receiverProfile = await userService.getProfile(receiverId);
        const senderProfile = await userService.getProfile(senderId);

        if (receiverProfile?.email && !receiverProfile?.blockedUsers?.includes(senderId)) {
          const senderName = senderProfile?.displayName || 'Someone';

          // 🔥 SEND PUSH NOTIFICATION (FCM)
          await notificationService.sendPush(receiverId, `New message from ${senderName}`, text, {
            chatId,
            type: 'chat',
          });

          // Trigger email notification via our backend (Now Secured)
          await notificationService.sendEmail(
            receiverProfile.email,
            `New message from ${senderName} on Pambo`,
            `You have a new message from ${senderName} regarding an ad:\n\n"${text}"\n\nLog in to Pambo to reply!`,
            `
              <div style="font-family: sans-serif; padding: 20px;">
                <h2 style="color: #FF6600;">You've got a new message on Pambo!</h2>
                <p><strong>${senderName}</strong> sent you a message:</p>
                <div style="background: #f9f9f9; padding: 15px; border-radius: 8px; border-left: 4px solid #FF6600; margin: 20px 0;">
                  ${text}
                </div>
                <p>Log in to Pambo to view the chat and reply.</p>
                <a href="https://pambo.biz/messages" style="background: #FF6600; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block; margin-top: 10px;">Reply to Message</a>
                <p style="margin-top: 30px; font-size: 12px; color: #666;">This is an automated notification from Pambo Marketplace.</p>
              </div>
            `
          );
        }
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, messagesPath);
    }
  },

  async markChatAsRead(chatId: string, userId: string): Promise<void> {
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        [`readBy_${userId}`]: true,
      });
    } catch (error) {
      console.error('Failed to mark chat as read:', error);
    }
  },

  async markChatAsUnread(chatId: string, userId: string): Promise<void> {
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        [`readBy_${userId}`]: false,
      });
    } catch (error) {
      console.error('Failed to mark chat as unread:', error);
    }
  },

  async deleteChat(chatId: string, userId: string): Promise<void> {
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        [`deleted_${userId}`]: true,
      });
    } catch (error) {
      console.error('Failed to delete chat:', error);
    }
  },

  async deleteMessage(chatId: string, messageId: string): Promise<void> {
    try {
      const { deleteDoc } = await import('firebase/firestore');
      const msgRef = doc(db, 'chats', chatId, 'messages', messageId);
      await deleteDoc(msgRef);
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  },

  async archiveChat(chatId: string, userId: string, isArchived: boolean): Promise<void> {
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        [`archived_${userId}`]: isArchived,
      });
    } catch (error) {
      console.error('Failed to archive chat:', error);
    }
  },

  async blockChatUser(chatId: string, userId: string, isBlocked: boolean): Promise<void> {
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        [`blocked_${userId}`]: isBlocked,
      });
    } catch (error) {
      console.error('Failed to block user:', error);
    }
  },

  async setTypingStatus(chatId: string, userId: string, isTyping: boolean): Promise<void> {
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        [`typing_${userId}`]: isTyping,
      });
    } catch {
      // Fail silently to prevent console logging on typing
    }
  },
};
